// frontend/src/components/Suppliers/SupplierTable.jsx
import React from 'react';
import { Edit2, Trash2, Star, Globe, Phone, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_STYLES = {
  active:   'bg-green-500/20  text-green-400  border border-green-500/30',
  inactive: 'bg-red-500/20    text-red-400    border border-red-500/30',
  pending:  'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
};

const SupplierTable = ({ suppliers, loading, onEdit, onDelete }) => {
  const { user } = useAuth();
  const userLevel = user?.permissions?.level ?? 0;
  const canDelete = [90, 100, 110].includes(userLevel);

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!suppliers.length) {
    return (
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
        <p className="text-gray-400 text-sm">No suppliers found. Add your first supplier.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 bg-gray-900/40">
              <th className="text-left text-gray-400 font-medium px-4 py-3">Supplier</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden md:table-cell">Category</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden lg:table-cell">Contact</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3 hidden lg:table-cell">Location</th>
              <th className="text-left text-gray-400 font-medium px-4 py-3">Status</th>
              <th className="text-right text-gray-400 font-medium px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {suppliers.map(s => (
              <tr key={s.id} className="hover:bg-gray-700/20 transition-colors">

                {/* Supplier name + preferred */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {s.is_preferred && (
                      <Star size={12} className="text-orange-400 fill-orange-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-white font-medium">{s.company_name}</p>
                      {s.trade_name && (
                        <p className="text-gray-500 text-xs">{s.trade_name}</p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Category */}
                <td className="px-4 py-3 hidden md:table-cell">
                  <div>
                    <p className="text-gray-300 text-xs">{s.category_name || '—'}</p>
                    {s.group_name && (
                      <p className="text-gray-500 text-xs">{s.group_name}</p>
                    )}
                  </div>
                </td>

                {/* Contact */}
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div className="space-y-1">
                    {s.contact_email && (
                      <a href={`mailto:${s.contact_email}`}
                         className="flex items-center gap-1 text-gray-400 hover:text-white text-xs transition-colors">
                        <Mail size={11} /> {s.contact_email}
                      </a>
                    )}
                    {s.contact_phone && (
                      <p className="flex items-center gap-1 text-gray-400 text-xs">
                        <Phone size={11} /> {s.contact_phone}
                      </p>
                    )}
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noreferrer"
                         className="flex items-center gap-1 text-gray-400 hover:text-white text-xs transition-colors">
                        <Globe size={11} /> Website
                      </a>
                    )}
                  </div>
                </td>

                {/* Location */}
                <td className="px-4 py-3 hidden lg:table-cell">
                  <p className="text-gray-400 text-xs">
                    {[s.city, s.state_region, s.country].filter(Boolean).join(', ') || '—'}
                  </p>
                </td>

                {/* Status */}
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[s.status] || ''}`}>
                    {s.status}
                  </span>
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(s)}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => onDelete(s.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer count */}
      <div className="px-4 py-3 border-t border-gray-700 bg-gray-900/20">
        <p className="text-gray-500 text-xs">{suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''}</p>
      </div>
    </div>
  );
};

export default SupplierTable;